import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BaseService } from './base.service';
import { environment } from '@env/environment';
import { EXCLUDE_NOTIFICATION_ONERROR } from './interceptors/error-interceptor';
import { BehaviorSubject } from 'rxjs';

export class VersionChanged{
    hasNewVersion: boolean;
    currentVersion: string;
    newVersion: string;
    constructor(hasNewVersion: boolean, currentVersion: string, newVersion: string) {
        this.hasNewVersion = hasNewVersion;
        this.currentVersion = currentVersion;
        this.newVersion = newVersion;
    }
}

@Injectable()
export class VersionCheckService extends BaseService {
    private readonly checkInterval = environment.VersionCheckInterval;
    private notFoundCounter = 0;
    private readonly maxNotFoundCount = 5; // Maximum number of 404 errors before stopping the check
    constructor(_http: HttpClient) {
        super(_http);
    }

    private versionChanged = new BehaviorSubject<VersionChanged>(new VersionChanged(false, environment.releaseVersion, environment.releaseVersion));

    versionChangedEvent$ = this.versionChanged.asObservable();
    private _timerInt: any = null;
    public startVersionCheck(): void {
        if (environment.EnableVersionCheck === false) {
            return;
        }
        this.checkVersion(); // Initial check
        this._timerInt = setInterval(async () => {
            const result = await this.checkVersion();
            if (!result) {
                console.warn("Error occurred checking version. Stopping background version check");
                clearInterval(this._timerInt); //stop checking if error
            }
        }, this.checkInterval);
    }

    public stopVersionCheck(): void {
        if (environment.EnableVersionCheck === false) {
            return;
        }
        console.warn("Stopping background version check");
        clearInterval(this._timerInt);
    }

    private async checkVersion(): Promise<boolean> {
        try {
            var ctx = new HttpContext();
            ctx.set(EXCLUDE_NOTIFICATION_ONERROR, true);
            const serverVersion = await this._http.get(environment.VersionRoute + `?bust=${Date.now()}`, { responseType: 'text', context: ctx }).toPromise();
            if (serverVersion) {
                if (serverVersion.trim() !== environment.releaseVersion) {
                    console.warn(
                        `New version detected: ${serverVersion.trim()}. Current version: ${environment.releaseVersion}`
                    );
                    this.versionChanged.next(new VersionChanged(true, environment.releaseVersion, serverVersion.trim()));
                }
            }
            return true;
        } catch (err) {
            console.error('Failed to fetch version.txt:', err);
            if (err.status === 404 && this.notFoundCounter < this.maxNotFoundCount) {
                this.notFoundCounter++;
                return true; // Ignore 404 errors
            }
            return false;
        }
    }

    public async forceReloadWithNavigation(): Promise<void> {
        this.setNoCacheMetaTags();

        await this.tryDeleteCaches();

        const anyWindow = window as any;
        try {

            if (anyWindow.navigation && typeof anyWindow.navigation.reload === 'function') {
                console.log('Using Navigation API to reload the page');
                anyWindow.navigation.reload();
            } else {
                console.log('Using window.location.reload() to reload the page');
                window.location.reload();
            }
        } catch (e) {
            console.warn('Navigation API not supported, falling back to alternative methods');
            window.location.reload();
        }
    }
    private async tryDeleteCaches(): Promise<void> {
        if ('caches' in window) {
            try {
                const cacheNames = await caches.keys();
                cacheNames.forEach(async cacheName => {
                    const cache = await caches.open(cacheName);
                    const cachedRequests = await cache.keys();
                    cachedRequests.forEach(async request => {
                        await cache.delete(request);
                    });
                });
            }
            catch (e) {
                console.warn('Cache API not supported, falling back to alternative methods');
            }
        }
    }
    private setNoCacheMetaTags(): void {
        const metaTags = [
            { httpEquiv: 'Cache-Control', content: 'no-cache, no-store, must-revalidate' },
            { httpEquiv: 'Pragma', content: 'no-cache' },
            { httpEquiv: 'Expires', content: '-1' }
        ];

        metaTags.forEach(tagInfo => {
            const meta = document.createElement('meta');
            meta.httpEquiv = tagInfo.httpEquiv;
            meta.content = tagInfo.content;
            document.head.appendChild(meta);
        });
    }
}
